/**
 * GENERATED FILE — do not edit by hand.
 *
 * Rendered from `src/app/data/datasets.source.json` by
 * `internals/scripts/generate-datasets.ts`. Edit the JSON source (or the
 * generator), then run `yarn datasets:generate`. `yarn datasets:check` fails
 * CI if this file drifts from a fresh render. The exported API below
 * (`datasets`, `getDataset`, `datasetsByCategory`, `catalogueStats`,
 * `COVERAGE_FIELDS`, and the `Dataset` types) is unchanged and stable.
 */

/**
 * The dataset registry — the single source of truth for catalogue metadata.
 *
 * Before this file, dataset facts lived in five unsynchronised places and had
 * already drifted apart. Everything that renders a dataset now reads from here.
 *
 * On honesty: fields we have not published are `undefined`, never guessed. The
 * UI renders those as "Not yet documented" rather than hiding the row, so the
 * gaps stay visible and can be closed deliberately. Do not populate a field
 * here until the number is verified — a wrong coverage figure on a catalogue
 * is worse than a missing one.
 *
 * Provenance of what is already filled in:
 *  - name, docsUrl, category    src/app/components/Landing/OptimizedAnimationBox.tsx
 *  - historyStart, entities,
 *    universe                   src/app/pages/FAQ/index.tsx (Universe Coverage table)
 *  - sdkQuery                   sovai python SDK v0.2.78, get_data.py — the
 *                               ENDPOINT_TO_TICKER / CLIENT_SIDE_ENDPOINT_*
 *                               registries, then confirmed by executing each
 *                               call. Every string here has returned a frame.
 *  - schema, columnCount,
 *    identifiers, rows,
 *    sampleTickers              measured from a live sov.data() pull.
 *
 * VERIFIED (do not re-guess):
 *  - `sdkQuery` for all 26 datasets that have one. The earlier docs-path
 *    inferences were roughly half wrong and have been replaced. Two datasets
 *    have `sdkQuery: undefined` because no sov.data() endpoint exists for them
 *    (SEC Edgar Search is the sov.sec_search() function; AirBnB Data has no
 *    registered endpoint at all). Do not invent one.
 *  - `schema` / `columnCount` / `identifiers` for the 25 datasets that returned
 *    a frame. Types are the pandas dtypes the SDK actually hands back.
 *
 * STILL UNVERIFIED — leave undefined until measured:
 *  - `rows` is set only where an unfiltered pull returned the complete dataset
 *    and `full_history=True` returned an identical shape (the five economic and
 *    sectorial time series, plus congress). Every ticker-partitioned dataset was
 *    sampled with a single-ticker filter, so its true row count is unknown.
 *  - `updateFrequency`, `publicationLag`, `pointInTime` for every dataset. The
 *    two updateFrequency values present predate this pass and are unsourced.
 *  - `sec/10k` returned no schema: it is keyed on `cik`, not ticker, and the
 *    pull did not complete inside a workable timeout. The endpoint string is
 *    from the SDK source and is trusted; its columns are not yet known.
 *  - `summary` lines were written from the dataset name and docs path. They are
 *    plausible, not authoritative. Review before they ship as product copy.
 *
 * KNOWN CONFLICTS between the FAQ table and the SDK (FAQ values left in place,
 * flagged here rather than silently overwritten):
 *  - congressional-data: FAQ historyStart 2011-01-01; the SDK's earliest
 *    observation is 2014-01-03.
 *  - box-office-stats: FAQ universe is US public, but the returned tickers
 *    include foreign listings (e.g. 6758, BOM:500111).
 *  - financial-ratios: FAQ entities 8522; the latest cross-section carries
 *    6276 tickers. Not necessarily contradictory — delisted names would only
 *    appear in full history — but it is unreconciled.
 */

export type DatasetCategory = 'equity' | 'economic' | 'sectorial';

/** How complete a dataset's coverage metadata is. Drives the UI's status chip. */
export type DatasetStatus = 'available' | 'beta';

/** Entity universe a dataset spans. Kept as a union so it stays comparable. */
export type Universe =
  | 'US public (listed & delisted)'
  | 'US public (listed & delisted) + foreign + private';

export interface Dataset {
  /** URL-safe identifier. This is the dataset's address: /datasets/<slug> */
  slug: string;
  name: string;
  category: DatasetCategory;
  status: DatasetStatus;
  /** Canonical documentation page on docs.sov.ai */
  docsUrl: string;

  /**
   * The SDK query string, i.e. sov.data("<sdkQuery>"). Taken from the sovai
   * package's endpoint registry and confirmed by executing the call.
   *
   * `undefined` means the dataset genuinely has no sov.data() endpoint — it is
   * reached another way, or not at all. Callers must handle that rather than
   * rendering a snippet that would error.
   */
  sdkQuery?: string;

  /** One-line description written for a quant scanning a list. */
  summary: string;

  // ---- Coverage (from the FAQ Universe Coverage table) --------------------

  /** ISO date of the earliest observation. Undefined while a dataset is in beta. */
  historyStart?: string;
  /** Approximate distinct entities covered, as published. May grow. */
  entities?: number;
  universe?: Universe;

  // ---- Not yet documented anywhere -----------------------------------------
  // These are the fields institutional buyers diligence on. They are declared
  // so the UI can render an explicit gap, and so filling them in is a data
  // task rather than a schema change. DO NOT GUESS THESE.

  /** How often new data lands, e.g. 'Daily', 'Weekly', 'Event-driven'. */
  updateFrequency?: string;
  /** Lag between the real-world event and availability. Distinct from cadence. */
  publicationLag?: string;
  /** Whether vintages are retained; free text, specific per dataset. */
  pointInTime?: string;
  /** Identifier columns actually present in the returned frame. Measured. */
  identifiers?: string[];
  /**
   * Row count. Set ONLY where an unfiltered pull returned the whole dataset.
   * Never extrapolated from a sample.
   */
  rows?: number;
  /**
   * Column schema as returned by the SDK. `type` is the pandas dtype.
   * Omitted for datasets too wide to enumerate usefully — `columnCount` still
   * carries the measured width in that case.
   */
  schema?: Array<{ name: string; type: string; description?: string }>;
  /**
   * Measured number of columns, including index columns. Present even when
   * `schema` is omitted for width.
   */
  columnCount?: number;
  /**
   * Real tickers observed in a returned frame, for constituent chips in the UI.
   * Populated only where an unfiltered pull surfaced genuine constituents — a
   * dataset sampled with a ticker filter would only echo the filter back, so it
   * is left undefined rather than being made to look like a universe.
   */
  sampleTickers?: string[];
}

export const datasets: Dataset[] = [
  {
    slug: 'accounting-data',
    name: 'Accounting Data',
    category: 'equity',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/accounting-data',
    sdkQuery: 'accounting/weekly',
    summary:
      'Standardised quarterly accounting line items for US listed and delisted companies.',
    historyStart: '2012-04-02',
    entities: 7395,
    universe: 'US public (listed & delisted)',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[ns]' },
      { name: 'cash_short_term', type: 'float32' },
      { name: 'operating_working_capital', type: 'float32' },
      { name: 'total_nonoperating_assets', type: 'float32' },
      { name: 'total_operating_assets', type: 'float32' },
      { name: 'accounts_receivable', type: 'float32' },
      { name: 'cash_equiv_usd', type: 'float32' },
      { name: 'cash_equivalents', type: 'float32' },
      { name: 'current_assets', type: 'float32' },
      { name: 'current_investments', type: 'float32' },
      { name: 'intangible_assets', type: 'float32' },
      { name: 'inventory_amount', type: 'float32' },
      { name: 'non_current_assets', type: 'float32' },
      { name: 'non_current_investments', type: 'float32' },
      { name: 'property_plant_equipment_net', type: 'float32' },
      { name: 'tax_assets', type: 'float32' },
      { name: 'total_assets', type: 'float32' },
      { name: 'total_investments', type: 'float32' },
      { name: 'accum_other_comp_income', type: 'float32' },
      { name: 'adjusted_parent_equity', type: 'float32' },
      { name: 'book_equity_value', type: 'float32' },
      { name: 'equity_usd', type: 'float32' },
      { name: 'retained_earnings', type: 'float32' },
      { name: 'total_equity', type: 'float32' },
      { name: 'accounts_payable', type: 'float32' },
      { name: 'bank_deposits', type: 'float32' },
      { name: 'current_debt', type: 'float32' },
      { name: 'current_liabilities', type: 'float32' },
      { name: 'debt_usd', type: 'float32' },
      { name: 'deferred_revenue', type: 'float32' },
      { name: 'non_current_debt', type: 'float32' },
      { name: 'non_current_liabilities', type: 'float32' },
      { name: 'tax_liabilities', type: 'float32' },
      { name: 'total_debt', type: 'float32' },
      { name: 'total_liabilities', type: 'float32' },
      { name: 'capital_expenditures', type: 'float32' },
      { name: 'depreciation_amortization', type: 'float32' },
      { name: 'net_cash_acquisitions_disposals', type: 'float32' },
      { name: 'net_cash_flow', type: 'float32' },
      { name: 'net_cash_flow_business', type: 'float32' },
      { name: 'net_cash_flow_common', type: 'float32' },
      { name: 'net_cash_flow_debt', type: 'float32' },
      { name: 'net_cash_flow_dividends', type: 'float32' },
      { name: 'net_cash_flow_financing', type: 'float32' },
      { name: 'net_cash_flow_fx', type: 'float32' },
      { name: 'net_cash_flow_investing', type: 'float32' },
      { name: 'net_cash_flow_operating', type: 'float32' },
      { name: 'operating_accruals', type: 'float32' },
      { name: 'operating_cash_flow', type: 'float32' },
      { name: 'stock_based_compensation', type: 'float32' },
      { name: 'total_accruals', type: 'float32' },
      { name: 'cost_of_revenue', type: 'float32' },
      { name: 'dividends_total', type: 'float32' },
      { name: 'interest_expense', type: 'float32' },
      { name: 'operating_expenses', type: 'float32' },
      { name: 'preferred_dividends', type: 'float32' },
      { name: 'research_development_expenses', type: 'float32' },
      { name: 'selling_general_admin_expenses', type: 'float32' },
      { name: 'tax_expenses', type: 'float32' },
      { name: 'cash_operating_profit', type: 'float32' },
      { name: 'comprehensive_net_income', type: 'float32' },
      { name: 'consolidated_income', type: 'float32' },
      { name: 'ebit', type: 'float32' },
      { name: 'ebit_usd', type: 'float32' },
      { name: 'gross_profit', type: 'float32' },
      { name: 'net_income', type: 'float32' },
      { name: 'net_income_common_stock', type: 'float32' },
      { name: 'net_income_common_stock_usd', type: 'float32' },
      { name: 'net_income_discontinued_ops', type: 'float32' },
      { name: 'net_income_excluding_discontinued', type: 'float32' },
      { name: 'net_income_non_controlling_int', type: 'float32' },
      { name: 'operating_income', type: 'float32' },
      { name: 'revenue_usd', type: 'float32' },
      { name: 'total_operating_net_income', type: 'float32' },
      { name: 'total_revenue', type: 'float32' },
      { name: 'tangible_assets', type: 'float32' },
      { name: 'working_capital', type: 'float32' },
      { name: 'free_cash_flow', type: 'float32' },
      { name: 'invested_capital', type: 'float32' },
      { name: 'earnings_before_tax', type: 'float32' },
      { name: 'ebitda', type: 'float32' },
      { name: 'enterprise_value', type: 'float32' },
    ],
    columnCount: 83,
  },
  {
    slug: 'bankruptcy-predictions',
    name: 'Bankruptcy Predictions',
    category: 'equity',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/bankruptcy-predictions',
    sdkQuery: 'bankruptcy',
    summary:
      'Modelled probability of corporate distress, with the deepest history in the catalogue.',
    historyStart: '1985-01-01',
    entities: 2000,
    universe: 'US public (listed & delisted)',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[us]' },
      { name: 'probability_light', type: 'float64' },
      { name: 'probability_convolution', type: 'float64' },
      { name: 'probability_rocket', type: 'float64' },
      { name: 'probability_encoder', type: 'float64' },
      { name: 'probability_fundamental', type: 'float64' },
      { name: 'probability', type: 'float64' },
      { name: 'sans_market', type: 'float64' },
      { name: 'volatility', type: 'float64' },
      { name: 'multiplier', type: 'float64' },
      { name: 'version', type: 'int64' },
    ],
    columnCount: 12,
  },
  {
    slug: 'employee-visa',
    name: 'Employee Visa',
    category: 'equity',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/employee-visa',
    sdkQuery: 'visas/h1b',
    summary:
      'H-1B and related visa filings by employer — a hiring signal that reaches private companies.',
    historyStart: '2001-01-01',
    entities: 2580,
    universe: 'US public (listed & delisted) + foreign + private',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[ns]' },
      { name: 'subsidiary_name', type: 'str' },
      { name: 'employer_name', type: 'str' },
      { name: 'similarity', type: 'float64' },
      { name: 'source', type: 'str' },
      { name: 'case_number', type: 'str' },
      { name: 'case_status', type: 'str' },
      { name: 'decision_date', type: 'datetime64[ns]' },
      { name: 'visa_class', type: 'str' },
      { name: 'begin_date', type: 'datetime64[ns]' },
      { name: 'end_date', type: 'datetime64[ns]' },
      { name: 'employer_address1', type: 'str' },
      { name: 'employer_city', type: 'str' },
      { name: 'employer_state', type: 'str' },
      { name: 'employer_postal_code', type: 'str' },
      { name: 'soc_code', type: 'str' },
      { name: 'soc_title', type: 'str' },
      { name: 'job_title', type: 'str' },
      { name: 'wage_rate_of_pay_from', type: 'float32' },
      { name: 'wage_rate_of_pay_to', type: 'float32' },
      { name: 'wage_unit_of_pay', type: 'str' },
      { name: 'full_time_position', type: 'str' },
      { name: 'worksite_address2', type: 'str' },
      { name: 'worksite_state', type: 'str' },
      { name: 'prevailing_wage', type: 'float32' },
      { name: 'pw_unit_of_pay', type: 'str' },
      { name: 'pw_survey_name', type: 'str' },
      { name: 'pw_other_source', type: 'str' },
      { name: 'pw_oes_year', type: 'float32' },
      { name: 'pw_survey_publisher', type: 'str' },
      { name: 'naics_code', type: 'float32' },
      { name: 'unique_id', type: 'str' },
      { name: 'wage_potential_increase', type: 'float32' },
      { name: 'predicted_pay', type: 'float32' },
      { name: 'visa_name', type: 'str' },
      { name: 'total_worker_positions', type: 'float64' },
      { name: 'new_employment', type: 'float64' },
      { name: 'continued_employment', type: 'float64' },
      { name: 'change_previous_employment', type: 'float64' },
      { name: 'new_concurrent_employment', type: 'float64' },
      { name: 'change_employer', type: 'float64' },
      { name: 'amended_petition', type: 'float64' },
    ],
    columnCount: 43,
  },
  {
    slug: 'earnings-surprise',
    name: 'Earnings Surprise',
    category: 'equity',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/earnings-surprise',
    sdkQuery: 'earnings/surprise',
    summary:
      'Realised versus expected earnings, aligned to the reporting date.',
    historyStart: '2003-09-17',
    entities: 2860,
    universe: 'US public (listed & delisted)',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[us]' },
      { name: 'surprise_probability', type: 'float64' },
      { name: 'eps_surprise', type: 'float64' },
      { name: 'actual_earning_result', type: 'float64' },
      { name: 'estimated_earning', type: 'float64' },
      { name: 'date_pub', type: 'str' },
    ],
    columnCount: 7,
  },
  {
    slug: 'congressional-data',
    name: 'Congressional Data',
    category: 'equity',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/congressional-data',
    sdkQuery: 'congress',
    summary: 'Disclosed securities trading by members of the US Congress.',
    historyStart: '2011-01-01',
    entities: 550,
    universe: 'US public (listed & delisted)',
    identifiers: ['ticker', 'date'],
    rows: 46282,
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[ns]' },
      { name: 'representative', type: 'str' },
      { name: 'bio_guide_id', type: 'str' },
      { name: 'transaction_date', type: 'datetime64[ns]' },
      { name: 'transaction', type: 'str' },
      { name: 'house', type: 'str' },
      { name: 'amount', type: 'str' },
      { name: 'party', type: 'str' },
      { name: 'last_modified', type: 'str' },
      { name: 'TickerType', type: 'str' },
      { name: 'Description', type: 'str' },
      { name: 'ExcessReturn', type: 'float64' },
      { name: 'PriceChange', type: 'float64' },
      { name: 'SPYChange', type: 'float64' },
      { name: 'days_to_report', type: 'int64' },
      { name: 'bio_guide_url', type: 'str' },
    ],
    columnCount: 17,
  },
  {
    slug: 'factor-signals',
    name: 'Factor Signals',
    category: 'equity',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/factor-signals',
    sdkQuery: 'factors/accounting',
    summary: 'Precomputed factor exposures spanning three decades of history.',
    historyStart: '1992-03-30',
    entities: 6582,
    universe: 'US public (listed & delisted)',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[ns]' },
      { name: 'profitability', type: 'float32' },
      { name: 'value', type: 'float32' },
      { name: 'solvency', type: 'float32' },
      { name: 'cash_flow', type: 'float32' },
      { name: 'illiquidity', type: 'float32' },
      { name: 'momentum_long_term', type: 'float32' },
      { name: 'momentum_medium_term', type: 'float32' },
      { name: 'short_term_reversal', type: 'float32' },
      { name: 'price_volatility', type: 'float32' },
      { name: 'dividend_yield', type: 'float32' },
      { name: 'earnings_consistency', type: 'float32' },
      { name: 'small_size', type: 'float32' },
      { name: 'low_growth', type: 'float32' },
      { name: 'low_equity_issuance', type: 'float32' },
      { name: 'bounce_dip', type: 'float32' },
      { name: 'accrual_growth', type: 'float32' },
      { name: 'low_depreciation_growth', type: 'float32' },
      { name: 'current_liquidity', type: 'float32' },
      { name: 'low_rnd', type: 'float32' },
      { name: 'momentum', type: 'float32' },
    ],
    columnCount: 22,
  },
  {
    slug: 'financial-ratios',
    name: 'Financial Ratios',
    category: 'equity',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/financial-ratios',
    sdkQuery: 'ratios/relative',
    summary:
      'Normalised financial ratios across the widest entity coverage in the catalogue.',
    historyStart: '1996-03-31',
    entities: 8522,
    universe: 'US public (listed & delisted)',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[ns]' },
      { name: 'closeadj', type: 'float64' },
      { name: 'volume', type: 'float64' },
      { name: 'market_cap', type: 'float64' },
      { name: 'evebit_ttm', type: 'float64' },
      { name: 'evebitda_ttm', type: 'float64' },
      { name: 'current_ratio', type: 'float64' },
      { name: 'quick_ratio', type: 'float64' },
      { name: 'cash_ratio', type: 'float64' },
      { name: 'operating_cash_flow_ratio', type: 'float64' },
      { name: 'net_working_capital_ratio', type: 'float64' },
      { name: 'acid_test_ratio', type: 'float64' },
      { name: 'excess_cash_margin_ratio', type: 'float64' },
      { name: 'earnings_per_share', type: 'float64' },
      { name: 'eps_usd', type: 'float64' },
      { name: 'eps_diluted', type: 'float64' },
      { name: 'gross_profit_margin', type: 'float64' },
      { name: 'operating_profit_margin', type: 'float64' },
      { name: 'ebitda_margin', type: 'float64' },
      { name: 'net_profit_margin', type: 'float64' },
      { name: 'return_on_assets', type: 'float64' },
      { name: 'return_on_equity', type: 'float64' },
      { name: 'return_on_net_assets', type: 'float64' },
      { name: 'roce_sub_cash', type: 'float64' },
      { name: 'roce_with_cash', type: 'float64' },
      { name: 'fcf_roce_with_cash', type: 'float64' },
      { name: 'fcf_roce_sub_cash', type: 'float64' },
      { name: 'income_dividend_payout_ratio', type: 'float64' },
      { name: 'return_on_invested_capital', type: 'float64' },
      { name: 'asset_turnover', type: 'float64' },
      { name: 'inventory_turnover', type: 'float64' },
      { name: 'days_sales_outstanding', type: 'float64' },
      { name: 'days_inventory_outstanding', type: 'float64' },
      { name: 'days_payable_outstanding', type: 'float64' },
      { name: 'cash_conversion_cycle', type: 'float64' },
      { name: 'total_asset_efficiency', type: 'float64' },
      { name: 'working_capital_turnover_ratio', type: 'float64' },
      { name: 'gross_operating_cycle', type: 'float64' },
      { name: 'sg_and_gross_profit_ratio', type: 'float64' },
      { name: 'depreciation_revenue_ratio', type: 'float64' },
      { name: 'depreciation_cfo_ratio', type: 'float64' },
      { name: 'debt_ratio', type: 'float64' },
      { name: 'equity_multiplier', type: 'float64' },
      { name: 'interest_coverage_ratio', type: 'float64' },
      { name: 'debt_to_capital', type: 'float64' },
      { name: 'debt_service_coverage', type: 'float64' },
      { name: 'liabilities_equity_ratio', type: 'float64' },
      { name: 'debt_ebitda_ratio', type: 'float64' },
      { name: 'debt_ebitda_minus_capex_ratio', type: 'float64' },
      { name: 'debt_equity_ratio', type: 'float64' },
      { name: 'ebitda_interest_coverage', type: 'float64' },
      { name: 'ebitda_minus_capex_interest_coverage', type: 'float64' },
      { name: 'interest_to_cfo_plus_interest_coverage', type: 'float64' },
      { name: 'debt_to_total_capital', type: 'float64' },
      { name: 'debt_cfo_ratio', type: 'float64' },
      { name: 'ltdebt_cfo_ratio', type: 'float64' },
      { name: 'ltdebt_earnings_ratio', type: 'float64' },
      { name: 'cash_flow_to_debt_ratio', type: 'float64' },
      { name: 'cash_flow_coverage_ratio', type: 'float64' },
      { name: 'operating_cash_flow_to_sales', type: 'float64' },
      { name: 'free_cash_flow_conversion_ratio', type: 'float64' },
      { name: 'rough_dividend_payout_ratio', type: 'float64' },
      { name: 'dividends_cfo_ratio', type: 'float64' },
      { name: 'preferred_cfo_ratio', type: 'float64' },
      { name: 'cash_flow_reinvestment_ratio', type: 'float64' },
      { name: 'free_cashflow_ps', type: 'float64' },
      { name: 'enterprise_value', type: 'float64' },
      { name: 'price_to_earnings', type: 'float64' },
      { name: 'price_to_book', type: 'float64' },
      { name: 'price_to_sales', type: 'float64' },
      { name: 'dividend_yield', type: 'float64' },
      { name: 'market_to_book_ratio', type: 'float64' },
      { name: 'ev_opinc_ratio', type: 'float64' },
      { name: 'rough_ffo', type: 'float64' },
      { name: 'dividend_payout_ratio_pref', type: 'float64' },
      { name: 'dividend_payout_ratio', type: 'float64' },
      { name: 'retention_ratio', type: 'float64' },
      { name: 'greenblatt_earnings_yield', type: 'float64' },
      { name: 'enterprise_value_to_revenue', type: 'float64' },
      { name: 'enterprise_value_to_ebitda', type: 'float64' },
      { name: 'enterprise_value_to_ebit', type: 'float64' },
      { name: 'enterprise_value_to_invested_capital', type: 'float64' },
      { name: 'enterprise_value_to_free_cash_flow', type: 'float64' },
      { name: 'cash_productivity_ratio', type: 'float64' },
      { name: 'debt_to_market_ratio', type: 'float64' },
      { name: 'net_debt_to_price_ratio', type: 'float64' },
      { name: 'cash_flow_to_price_ratio', type: 'float64' },
      { name: 'rd_to_market_ratio', type: 'float64' },
      { name: 'book_to_market_enterprise_value_ratio', type: 'float64' },
      { name: 'equity_payout_yield', type: 'float64' },
      { name: 'equity_net_payout_yield', type: 'float64' },
      { name: 'ebitda_to_mev_ratio', type: 'float64' },
    ],
    columnCount: 93,
    sampleTickers: [
      'A',
      'AA',
      'AAC.U',
      'AACB',
      'AACBU',
      'AACG',
      'AACI',
      'AACIU',
    ],
  },
  {
    slug: 'government-contracts',
    name: 'Government Contracts',
    category: 'equity',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/government-contracts',
    sdkQuery: 'spending/contracts',
    summary:
      'Federal contract awards mapped to issuers, including foreign and private counterparties.',
    historyStart: '2013-10-01',
    entities: 5900,
    universe: 'US public (listed & delisted) + foreign + private',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[us]' },
      { name: 'source', type: 'str' },
      { name: 'contract_award_unique_key', type: 'str' },
      { name: 'awarding_agency_name', type: 'str' },
      { name: 'awarding_sub_agency_name', type: 'str' },
      { name: 'solicitation_date', type: 'str' },
      { name: 'solicitation_procedures', type: 'str' },
      { name: 'type_of_contract_pricing', type: 'str' },
      { name: 'award_type', type: 'str' },
      { name: 'number_of_offers_received', type: 'float64' },
      { name: 'usaspending_permalink', type: 'str' },
    ],
    columnCount: 12,
  },
  {
    slug: 'institutional-trading',
    name: 'Institutional Trading',
    category: 'equity',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/institutional-trading',
    sdkQuery: 'institutional/trading',
    summary: 'Institutional flow and holdings derived from regulatory filings.',
    historyStart: '2008-06-30',
    entities: 4760,
    universe: 'US public (listed & delisted)',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[ns]' },
      { name: 'std_percentoftotal_fund_median', type: 'float32' },
      { name: 'std_put_call_ratio_fund_median', type: 'float32' },
      { name: 'std_derivative_ratio_fund_median', type: 'float32' },
      { name: 'derivative_ratio_fund_median', type: 'float32' },
      { name: 'put_call_ratio_fund_median', type: 'float32' },
      { name: 'fund_ratio_fund_median', type: 'float32' },
      { name: 'debt_ratio_fund_median', type: 'float32' },
      { name: 'preferred_ratio_fund_median', type: 'float32' },
      { name: 'totalvalue_median', type: 'float32' },
      { name: 'percentoftotal_median', type: 'float32' },
      { name: 'shrholdings_growth_median', type: 'float32' },
      { name: 'totalvalue_growth_median', type: 'float32' },
      { name: 'put_call_ratio_fund_growth_median', type: 'float32' },
      { name: 'derivative_ratio_fund_growth_median', type: 'float32' },
      { name: 'market_tilt_pca_median', type: 'float32' },
      { name: 'sector_tilt_pca_median', type: 'float32' },
      { name: 'strategy_tilt_pca_median', type: 'float32' },
      { name: 'quantitative_tilt_pca_median', type: 'float32' },
      { name: 'instrument_tilt_pca_median', type: 'float32' },
      { name: 'weight_variability_median', type: 'float32' },
      { name: 'weight_mean_median', type: 'float32' },
      { name: 'weight_coff_variance_median', type: 'float32' },
      { name: 'weight_max_median', type: 'float32' },
      { name: 'weight_kurtosis_median', type: 'float32' },
      { name: 'weight_skew_median', type: 'float32' },
      { name: 'num_investments_median', type: 'float32' },
      { name: 'new_investments_median', type: 'float32' },
      { name: 'divestments_median', type: 'float32' },
      { name: 'new_investments_to_divestments_median', type: 'float32' },
      { name: 'portfolio_turnover_median', type: 'float32' },
      { name: 'net_change_to_investments_median', type: 'float32' },
      { name: 'uncorrelated_percentile_median', type: 'float32' },
      { name: 'flow_percentage_mean_median', type: 'float32' },
      { name: 'performance_value_mean_median', type: 'float32' },
      { name: 'fund_return_quarter_median', type: 'float32' },
      { name: 'fund_flows_percent_quarter_median', type: 'float32' },
      { name: 'std_percentoftotal_fund_std', type: 'float32' },
      { name: 'std_put_call_ratio_fund_std', type: 'float32' },
      { name: 'std_derivative_ratio_fund_std', type: 'float32' },
      { name: 'derivative_ratio_fund_std', type: 'float32' },
      { name: 'put_call_ratio_fund_std', type: 'float32' },
      { name: 'fund_ratio_fund_std', type: 'float32' },
      { name: 'debt_ratio_fund_std', type: 'float32' },
      { name: 'preferred_ratio_fund_std', type: 'float32' },
      { name: 'totalvalue_std', type: 'float32' },
      { name: 'percentoftotal_std', type: 'float32' },
      { name: 'shrholdings_growth_std', type: 'float32' },
      { name: 'totalvalue_growth_std', type: 'float32' },
      { name: 'put_call_ratio_fund_growth_std', type: 'float32' },
      { name: 'derivative_ratio_fund_growth_std', type: 'float32' },
      { name: 'market_tilt_pca_std', type: 'float32' },
      { name: 'sector_tilt_pca_std', type: 'float32' },
      { name: 'strategy_tilt_pca_std', type: 'float32' },
      { name: 'quantitative_tilt_pca_std', type: 'float32' },
      { name: 'instrument_tilt_pca_std', type: 'float32' },
      { name: 'weight_variability_std', type: 'float32' },
      { name: 'weight_mean_std', type: 'float32' },
      { name: 'weight_coff_variance_std', type: 'float32' },
      { name: 'weight_max_std', type: 'float32' },
      { name: 'weight_kurtosis_std', type: 'float32' },
      { name: 'weight_skew_std', type: 'float32' },
      { name: 'num_investments_std', type: 'float32' },
      { name: 'new_investments_std', type: 'float32' },
      { name: 'divestments_std', type: 'float32' },
      { name: 'new_investments_to_divestments_std', type: 'float32' },
      { name: 'portfolio_turnover_std', type: 'float32' },
      { name: 'net_change_to_investments_std', type: 'float32' },
      { name: 'uncorrelated_percentile_std', type: 'float32' },
      { name: 'flow_percentage_mean_std', type: 'float32' },
      { name: 'performance_value_mean_std', type: 'float32' },
      { name: 'fund_return_quarter_std', type: 'float32' },
      { name: 'fund_flows_percent_quarter_std', type: 'float32' },
      { name: 'totalvalue', type: 'float32' },
      { name: 'total_derivatives', type: 'float32' },
      { name: 'percentoftotal', type: 'float32' },
      { name: 'growth_totalvalue', type: 'float32' },
      { name: 'growth_shrholders', type: 'float32' },
      { name: 'growth_shrvalue', type: 'float32' },
      { name: 'growth_percentoftotal', type: 'float32' },
      { name: 'growth_shrholder_value_divergence', type: 'float32' },
      { name: 'diversification_score_ticker', type: 'float32' },
      { name: 'derivative_ratio_ticker', type: 'float32' },
      { name: 'derivative_holder_ratio', type: 'float32' },
      { name: 'derivative_holder_value_divergence', type: 'float32' },
      { name: 'short_interest', type: 'float32' },
      { name: 'security_concentration', type: 'float32' },
      { name: 'put_call_ratio_ticker', type: 'float32' },
      { name: 'put_call_holder_ratio', type: 'float32' },
      { name: 'put_holder_value_sentiment_divergence', type: 'float32' },
      { name: 'value_per_holder', type: 'float32' },
      { name: 'debt_equity_ratio', type: 'float32' },
      { name: 'historical_high_sharevalue', type: 'float32' },
      { name: 'percentage_from_high_sharevalue', type: 'float32' },
      { name: 'previous_high_sharevalue', type: 'float32' },
      { name: 'percentage_above_previous_high', type: 'float32' },
      { name: 'overweight', type: 'float32' },
      { name: 'allocation_pressure_percentile', type: 'float32' },
      { name: 'net_flows_sum', type: 'float32' },
      { name: 'net_flows_max', type: 'float32' },
      { name: 'net_flows_std', type: 'float32' },
      { name: 'net_flows_mean', type: 'float32' },
      { name: 'net_flows_inflow_outflow_value_ratio', type: 'float32' },
      { name: 'net_flows_inflow_outflow_count_ratio', type: 'float32' },
      { name: 'appreciation_value_sum', type: 'float32' },
      { name: 'new_value_sum', type: 'float32' },
      { name: 'turnover_percentage_median', type: 'float32' },
      { name: 'quarter_return', type: 'float32' },
      { name: 'quarter_flows', type: 'float32' },
      { name: 'derivative_overloaded', type: 'float32' },
      { name: 'put_overloaded', type: 'float32' },
    ],
    columnCount: 112,
  },
  {
    slug: 'insider-flow-prediction',
    name: 'Insider Flow Prediction',
    category: 'equity',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/insider-flow-prediction',
    sdkQuery: 'insider/flow_prediction',
    summary: 'Modelled insider transaction flow and its forward signal.',
    historyStart: '2013-08-15',
    entities: 3700,
    universe: 'US public (listed & delisted)',
    schema: [{ name: 'flow_prediction', type: 'float32' }],
    columnCount: 1,
  },
  {
    slug: 'liquidity-data',
    name: 'Liquidity Data',
    category: 'equity',
    status: 'beta',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/liquidity-data',
    sdkQuery: 'liquidity/price_improvement',
    summary:
      'Price improvement and market opportunity measures. Two beta series.',
    universe: 'US public (listed & delisted)',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[us]' },
      { name: 'total_price_improvement', type: 'float64' },
      { name: 'shares', type: 'int64' },
      { name: 'price_improvement_per_share', type: 'float64' },
      { name: 'average_price_improvement', type: 'float64' },
    ],
    columnCount: 6,
  },
  {
    slug: 'lobbying-data',
    name: 'Lobbying Data',
    category: 'equity',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/lobbying-data',
    sdkQuery: 'lobbying/public',
    summary:
      'Disclosed federal lobbying spend — the broadest entity coverage in the catalogue.',
    historyStart: '1999-12-31',
    entities: 14500,
    universe: 'US public (listed & delisted) + foreign + private',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[ns]' },
      { name: 'client', type: 'str' },
      { name: 'client_description', type: 'str' },
      { name: 'spend', type: 'float64' },
      { name: 'transaction_type', type: 'str' },
      { name: 'filing_type', type: 'str' },
      { name: 'lobby_description', type: 'object' },
      { name: 'issue_codes', type: 'object' },
      { name: 'government_entity_details', type: 'object' },
      { name: 'quarter', type: 'str' },
      { name: 'effective_date', type: 'str' },
      { name: 'termination_date', type: 'str' },
      { name: 'client_state', type: 'str' },
      { name: 'client_country', type: 'str' },
      { name: 'client_id', type: 'int64' },
      { name: 'government_lobby', type: 'float64' },
      { name: 'performing_own_lobbying', type: 'float64' },
      { name: 'registrant_dt_updated', type: 'str' },
      { name: 'registrant_name', type: 'str' },
      { name: 'registrant_address', type: 'str' },
      { name: 'registrant_id', type: 'int64' },
      { name: 'registrant_description', type: 'str' },
      { name: 'registrant_contact_name', type: 'str' },
      { name: 'registrant_house_registrant_id', type: 'float64' },
      { name: 'registrant_contact_telephone', type: 'str' },
      { name: 'lobbyist_full_names', type: 'object' },
      { name: 'lobbyist_ids', type: 'object' },
      { name: 'previous_goverment_positions', type: 'object' },
      { name: 'lobbyist_new_statuses', type: 'object' },
      { name: 'client_url', type: 'str' },
      { name: 'registrant_url', type: 'str' },
      { name: 'filing_url', type: 'str' },
      { name: 'filing_id', type: 'str' },
      { name: 'unique_id', type: 'str' },
      { name: 'match', type: 'str' },
      { name: 'date_time', type: 'datetime64[ns]' },
    ],
    columnCount: 37,
  },
  {
    slug: 'news-sentiment',
    name: 'News Sentiment',
    category: 'equity',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/news-sentiment',
    sdkQuery: 'news/sentiment',
    summary: 'Ticker-linked news sentiment scores and variants.',
    historyStart: '2017-01-01',
    entities: 2000,
    universe: 'US public (listed & delisted)',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[ns]' },
      { name: 'sentiment', type: 'float32' },
    ],
    columnCount: 3,
  },
  {
    slug: 'price-breakout',
    name: 'Price Breakout',
    category: 'equity',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/price-breakout',
    sdkQuery: 'breakout',
    summary: 'Technical breakout classification, updated daily.',
    historyStart: '2022-03-10',
    entities: 5400,
    universe: 'US public (listed & delisted)',
    updateFrequency: 'Daily',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[ns]' },
      { name: 'target', type: 'float64' },
      { name: 'future_returns', type: 'float32' },
      { name: 'prediction', type: 'float64' },
      { name: 'bottom_prediction', type: 'float64' },
      { name: 'top_prediction', type: 'float64' },
      { name: 'standard_deviation', type: 'float64' },
      { name: 'bottom_conformal', type: 'float64' },
      { name: 'top_conformal', type: 'float64' },
      { name: 'slope', type: 'float64' },
    ],
    columnCount: 11,
  },
  {
    slug: 'risk-indicators',
    name: 'Risk Indicators',
    category: 'equity',
    status: 'beta',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/risk-indicators',
    sdkQuery: 'corprisk/risks',
    summary: 'Composite risk measures at the issuer level.',
    universe: 'US public (listed & delisted)',
    updateFrequency: 'Weekly',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[ns]' },
      { name: 'accounting', type: 'float32' },
      { name: 'accounting_ind_adjs', type: 'float32' },
      { name: 'misstatement', type: 'float32' },
      { name: 'misstatement_ind_adjs', type: 'float32' },
      { name: 'events', type: 'float32' },
      { name: 'events_ind_adjs', type: 'float32' },
      { name: 'risk', type: 'float32' },
      { name: 'risk_ind_adjs', type: 'float32' },
    ],
    columnCount: 10,
  },
  {
    slug: 'sec-edgar-search',
    name: 'SEC Edgar Search',
    category: 'equity',
    status: 'beta',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/sec-edgar-search',
    summary:
      'Full-text search tooling across EDGAR filings. Exposed as sov.sec_search(), not as a sov.data() endpoint.',
    universe: 'US public (listed & delisted)',
  },
  {
    slug: 'sec-10k-filings',
    name: 'SEC 10-K Filings',
    category: 'equity',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/sec-10k-filings',
    sdkQuery: 'sec/10k',
    summary: 'Parsed annual report filings with section-level structure.',
    historyStart: '1998-03-31',
    entities: 4762,
    universe: 'US public (listed & delisted)',
    identifiers: ['cik'],
  },
  {
    slug: 'short-selling',
    name: 'Short Selling',
    category: 'equity',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/short-selling',
    sdkQuery: 'short/over_shorted',
    summary: 'Short interest and over-shorted measures, updated weekly.',
    historyStart: '1998-01-02',
    entities: 5981,
    universe: 'US public (listed & delisted)',
    updateFrequency: 'Weekly',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[ns]' },
      { name: 'over_shorted', type: 'float32' },
      { name: 'over_shorted_chg', type: 'float32' },
      { name: 'short_interest', type: 'float32' },
      { name: 'number_of_shares', type: 'float32' },
      { name: 'short_percentage', type: 'float32' },
      { name: 'short_prediction', type: 'float32' },
      { name: 'days_to_cover', type: 'float32' },
      { name: 'market_cap', type: 'float32' },
      { name: 'total_revenue', type: 'float32' },
      { name: 'volume', type: 'float32' },
    ],
    columnCount: 12,
  },
  {
    slug: 'wikipedia-views',
    name: 'Wikipedia Views',
    category: 'equity',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/equity-datasets/wikipedia-views',
    sdkQuery: 'wikipedia/views',
    summary: 'Company page view counts as an attention proxy.',
    historyStart: '2015-07-31',
    entities: 3733,
    universe: 'US public (listed & delisted)',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[ns]' },
      { name: 'views', type: 'int64' },
      { name: 'relative_views', type: 'float32' },
      { name: 'alpha_short', type: 'float32' },
      { name: 'beta_short', type: 'float32' },
      { name: 'alpha_long', type: 'float32' },
      { name: 'beta_long', type: 'float32' },
      { name: 'long_short_alpha', type: 'float32' },
      { name: 'long_short_beta', type: 'float32' },
      { name: 'search_pressure', type: 'float32' },
    ],
    columnCount: 11,
  },

  // ---- Economic -----------------------------------------------------------
  // NOTE: the entire economic category is absent from the FAQ coverage table,
  // so historyStart / entities / universe are genuinely unknown here.
  {
    slug: 'asset-rotation',
    name: 'Asset Rotation',
    category: 'economic',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/economic-datasets/asset-rotation',
    sdkQuery: 'allocation/all',
    summary:
      'Cross-asset rotation weights across the dollar, real estate, commodities, bonds and equities.',
    identifiers: ['date'],
    rows: 881,
    schema: [
      { name: 'date', type: 'datetime64[us]' },
      { name: 'dollar', type: 'float64' },
      { name: 'real_estate', type: 'float64' },
      { name: 'commodities', type: 'float64' },
      { name: 'bonds', type: 'float64' },
      { name: 'equities', type: 'float64' },
    ],
    columnCount: 6,
  },
  {
    slug: 'core-economic-data',
    name: 'Core Economic Data',
    category: 'economic',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/economic-datasets/core-economic-data',
    sdkQuery: 'macro/features',
    summary:
      'Headline US macroeconomic series in a single frame, keyed on FRED-style codes.',
    identifiers: ['date'],
    rows: 786,
    columnCount: 276,
  },
  {
    slug: 'government-traffic',
    name: 'Government Traffic',
    category: 'economic',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/economic-datasets/government-traffic',
    sdkQuery: 'government/traffic/agencies',
    summary:
      'Traffic to federal agency web properties as an activity proxy. A sibling endpoint, government/traffic/domains, breaks the same series out by individual domain.',
    identifiers: ['date'],
    rows: 3225,
    schema: [
      { name: 'date', type: 'str' },
      { name: 'access-board', type: 'float64' },
      { name: 'agency-global-media', type: 'float64' },
      { name: 'agency-international-development', type: 'float64' },
      { name: 'agriculture', type: 'float64' },
      { name: 'american-battle-monuments-commission', type: 'float64' },
      { name: 'commerce', type: 'float64' },
      { name: 'commission-civil-rights', type: 'float64' },
      { name: 'commodity-futures-trading-commission', type: 'float64' },
      { name: 'consumer-financial-protection-bureau', type: 'float64' },
      { name: 'consumer-product-safety-commission', type: 'float64' },
      { name: 'corporation-national-community-service', type: 'float64' },
      { name: 'defense', type: 'float64' },
      { name: 'defense-nuclear-facilities-safety-board', type: 'float64' },
      { name: 'education', type: 'float64' },
      { name: 'energy', type: 'float64' },
      { name: 'environmental-protection-agency', type: 'float64' },
      { name: 'equal-employment-opportunity-commission', type: 'float64' },
      { name: 'executive-office-president', type: 'float64' },
      { name: 'farm-credit-administration', type: 'float64' },
      { name: 'federal-deposit-insurance-corporation', type: 'float64' },
      { name: 'federal-election-commission', type: 'float64' },
      { name: 'federal-energy-regulatory-commission', type: 'float64' },
      { name: 'federal-housing-finance-agency', type: 'float64' },
      { name: 'federal-maritime-commission', type: 'float64' },
      { name: 'federal-mine-safety-health-review-commission', type: 'float64' },
      { name: 'federal-retirement-thrift-investment-board', type: 'float64' },
      { name: 'federal-trade-commission', type: 'float64' },
      { name: 'general-services-administration', type: 'float64' },
      { name: 'health-human-services', type: 'float64' },
      { name: 'homeland-security', type: 'float64' },
      { name: 'housing-urban-development', type: 'float64' },
      { name: 'inter-american-foundation', type: 'float64' },
      { name: 'interior', type: 'float64' },
      {
        name: 'international-development-finance-corporation',
        type: 'float64',
      },
      { name: 'international-trade-commission', type: 'float64' },
      { name: 'justice', type: 'float64' },
      { name: 'labor', type: 'float64' },
      { name: 'merit-systems-protection-board', type: 'float64' },
      { name: 'millennium-challenge-corporation', type: 'float64' },
      { name: 'national-aeronautics-space-administration', type: 'float64' },
      { name: 'national-archives-records-administration', type: 'float64' },
      { name: 'national-capital-planning-commission', type: 'float64' },
      { name: 'national-council-disability', type: 'float64' },
      { name: 'national-credit-union-administration', type: 'float64' },
      { name: 'national-education-association', type: 'float64' },
      { name: 'national-endowment-humanities', type: 'float64' },
      { name: 'national-labor-relations-board', type: 'float64' },
      { name: 'national-mediation-board', type: 'float64' },
      { name: 'national-reconnaissance-office', type: 'float64' },
      { name: 'national-science-foundation', type: 'float64' },
      { name: 'national-transportation-safety-board', type: 'float64' },
      { name: 'nuclear-regulatory-commission', type: 'float64' },
      { name: 'nuclear-waste-technical-review-board', type: 'float64' },
      { name: 'office-director-national-intelligence', type: 'float64' },
      { name: 'office-government-ethics', type: 'float64' },
      { name: 'office-navajo-hopi-indian-relocation', type: 'float64' },
      { name: 'office-personnel-management', type: 'float64' },
      { name: 'peace-corps', type: 'float64' },
      { name: 'pension-benefit-guaranty-corporation', type: 'float64' },
      { name: 'postal-inspection-service', type: 'float64' },
      { name: 'postal-regulatory-commission', type: 'float64' },
      { name: 'postal-service', type: 'float64' },
      { name: 'privacy-civil-liberties-oversight-board', type: 'float64' },
      { name: 'public-buildings-reform-board', type: 'float64' },
      { name: 'railroad-retirement-board', type: 'float64' },
      { name: 'securities-exchange-commission', type: 'float64' },
      { name: 'small-business-administration', type: 'float64' },
      { name: 'social-security-administration', type: 'float64' },
      {
        name: 'special-inspector-general-afghanistan-restoration',
        type: 'float64',
      },
      { name: 'state', type: 'float64' },
      { name: 'surface-transportation-board', type: 'float64' },
      { name: 'transportation', type: 'float64' },
      { name: 'treasury', type: 'float64' },
      { name: 'udall-foundation', type: 'float64' },
      { name: 'veterans-affairs', type: 'float64' },
    ],
    columnCount: 76,
  },
  {
    slug: 'turing-risk-index',
    name: 'Turing Risk Index',
    category: 'economic',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/economic-datasets/turing-risk-index',
    sdkQuery: 'risks',
    summary:
      'Composite systemic risk index for the US economy, alongside its component volatility and recession measures.',
    identifiers: ['Date'],
    rows: 24204,
    schema: [
      { name: 'Date', type: 'datetime64[ns]' },
      { name: 'VIX', type: 'float64' },
      { name: 'HS', type: 'float64' },
      { name: 'MA', type: 'float64' },
      { name: 'EWMA', type: 'float64' },
      { name: 'GARCH', type: 'float64' },
      { name: 'ENSEMBLE', type: 'float64' },
      { name: 'RECESSION_6', type: 'float64' },
      { name: 'RECESSION_12', type: 'float64' },
      { name: 'RECESSION_24', type: 'float64' },
      { name: 'TURBULENCE', type: 'float64' },
      { name: 'SYSTEMIC', type: 'float64' },
      { name: 'CAPE', type: 'float64' },
      { name: 'MARKET_RISK', type: 'float64' },
      { name: 'SP500', type: 'float64' },
      { name: 'ADS_BUSINESS_NEG', type: 'float64' },
      { name: 'CREDIT_SPREAD', type: 'float64' },
      { name: 'TERM_SPREAD', type: 'float64' },
      { name: 'NEWS_SENT_NEG', type: 'float64' },
      { name: 'CORP_BOND_DISTRESS', type: 'float64' },
      { name: 'NAIIM_NEG', type: 'float64' },
      { name: 'AAII_NEG', type: 'float64' },
      { name: 'WEI_NEG', type: 'float64' },
      { name: 'ATLANTA_UNC', type: 'float64' },
      { name: 'CFSEC_NEG', type: 'float64' },
      { name: 'CFNAI_FNEG', type: 'float64' },
      { name: 'BUILDING_INDEX_NEG', type: 'float64' },
      { name: 'CONSUMER_INDEX_NEG', type: 'float64' },
      { name: 'INDUSTRY_INDEX_NEG', type: 'float64' },
      { name: 'MAIN_INDEX_NEG', type: 'float64' },
      { name: 'RETAIL_INDEX_NEG', type: 'float64' },
      { name: 'SERVICES_INDEX_NEG', type: 'float64' },
      { name: 'MAN_NY_NEG', type: 'float64' },
      { name: 'MAN_PHIL_NEG', type: 'float64' },
      { name: 'MICS_ICS_NEG', type: 'float64' },
      { name: 'MICS_ICC_NEG', type: 'float64' },
      { name: 'MICS_ICE_NEG', type: 'float64' },
      { name: 'NEW_TRUCKS', type: 'float64' },
      { name: 'NEW_HOMES', type: 'float64' },
      { name: 'MISERY_INDEX', type: 'float64' },
      { name: 'HOUSING_AFFORD_NEG', type: 'float64' },
      { name: 'NONMAN_OUTLOOK_NEG', type: 'float64' },
      { name: 'MAN_TEX_NEG', type: 'float64' },
      { name: 'ZEW_SENT_NEG', type: 'float64' },
      { name: 'ZEW_ACTUAL_NEG', type: 'float64' },
      { name: 'BUSINESS_RISK', type: 'float64' },
      { name: 'US_POLICY_UNC_D', type: 'float64' },
      { name: 'UK_POLICY_UNC_D', type: 'float64' },
      { name: 'CHINA_POLICY_UNC_M', type: 'float64' },
      { name: 'US_MARKET_UNC_D', type: 'float64' },
      { name: 'US_POLICY_VOL_M', type: 'float64' },
      { name: 'GLOBAL_POLICY_UNC_M', type: 'float64' },
      { name: 'US_SOVEREIGN_UNC_M', type: 'float64' },
      { name: 'GEO_UNC_D', type: 'float64' },
      { name: 'GEO_UNC_M', type: 'float64' },
      { name: 'GEO_EQUAL_M', type: 'float64' },
      { name: 'WEB_SEARCH_UNC_M', type: 'float64' },
      { name: 'THINKTANK_UNC_M', type: 'float64' },
      { name: 'POLITICAL_RISK', type: 'float64' },
      { name: 'TURING_RISK', type: 'float64' },
    ],
    columnCount: 60,
  },

  // ---- Sectorial ----------------------------------------------------------
  {
    slug: 'box-office-stats',
    name: 'Box Office Stats',
    category: 'sectorial',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/sectorial-datasets/box-office-stats',
    sdkQuery: 'movies/boxoffice',
    summary: 'Theatrical box office receipts mapped to listed studios.',
    historyStart: '1997-07-01',
    entities: 100,
    universe: 'US public (listed & delisted)',
    identifiers: ['ticker', 'date'],
    rows: 238362,
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[ns]' },
      { name: 'title', type: 'string' },
      { name: 'distributor', type: 'str' },
      { name: 'gross', type: 'Int64' },
      { name: 'percent_yd', type: 'Float64' },
      { name: 'percent_lw', type: 'Float64' },
      { name: 'theaters', type: 'Float64' },
      { name: 'per_theater', type: 'Float64' },
      { name: 'total_gross', type: 'Int64' },
      { name: 'days_in_release', type: 'Int64' },
      { name: 'parent company', type: 'str' },
      { name: 'distributor address', type: 'str' },
      { name: 'distributorwebsite', type: 'str' },
      { name: 'release_date', type: 'datetime64[ns]' },
    ],
    columnCount: 15,
    sampleTickers: [
      '600579',
      '6758',
      '8053',
      'ABS',
      'AMC',
      'AMCX',
      'AMPY',
      'AMZN',
    ],
  },
  {
    slug: 'cfpb-complaints',
    name: 'CFPB Complaints',
    category: 'sectorial',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/sectorial-datasets/cfpb-complaints',
    sdkQuery: 'complaints/public',
    summary: 'Consumer financial complaints mapped to the named institution.',
    historyStart: '2011-12-01',
    entities: 1000,
    universe: 'US public (listed & delisted)',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[ns]' },
      { name: 'company', type: 'str' },
      { name: 'bloomberg_share_id', type: 'str' },
      { name: 'culpability_score', type: 'float64' },
      { name: 'complaint_score', type: 'float64' },
      { name: 'grievance_score', type: 'float64' },
      { name: 'total_risk_rating', type: 'float64' },
      { name: 'product', type: 'str' },
      { name: 'sub_product', type: 'str' },
      { name: 'issue', type: 'str' },
      { name: 'sub_issue', type: 'str' },
      { name: 'consumer_complaint_narrative', type: 'str' },
      { name: 'company_public_response', type: 'str' },
      { name: 'state', type: 'str' },
      { name: 'zip_code', type: 'str' },
      { name: 'tags', type: 'str' },
      { name: 'consumer_consent_provided', type: 'str' },
      { name: 'submitted_via', type: 'str' },
      { name: 'date_sent_to_company', type: 'datetime64[ns]' },
      { name: 'company_response_to_consumer', type: 'str' },
      { name: 'timely_response', type: 'str' },
      { name: 'consumer_disputed', type: 'str' },
      { name: 'selected_name', type: 'str' },
      { name: 'similarity', type: 'float64' },
    ],
    columnCount: 25,
  },
  {
    slug: 'pharma-clinical-trials',
    name: 'Pharma Clinical Trials',
    category: 'sectorial',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/sectorial-datasets/pharma-clinical-trials',
    sdkQuery: 'clinical/trials',
    summary:
      'Trial registrations, phases and outcomes, including private sponsors.',
    historyStart: '1999-11-01',
    entities: 850,
    universe: 'US public (listed & delisted) + foreign + private',
    identifiers: ['ticker', 'date'],
    schema: [
      { name: 'ticker', type: 'str' },
      { name: 'date', type: 'datetime64[ns]' },
      { name: 'source', type: 'str' },
      { name: 'subsidiary', type: 'str' },
      { name: 'sponsor', type: 'str' },
      { name: 'trial_id', type: 'str' },
      { name: 'official_title', type: 'str' },
      { name: 'sponsor_study_id', type: 'str' },
      { name: 'brief_title', type: 'str' },
      { name: 'lead_sponsor', type: 'str' },
      { name: 'lead_sponsor_name', type: 'str' },
      { name: 'sponsor_type', type: 'str' },
      { name: 'lead_sponsor_type', type: 'str' },
      { name: 'phase_category', type: 'str' },
      { name: 'study_type', type: 'str' },
      { name: 'enrollment_count', type: 'float64' },
      { name: 'number_of_locations', type: 'int64' },
      { name: 'healthy_volunteers', type: 'bool' },
      { name: 'enrollment_type', type: 'str' },
      { name: 'study_size_category', type: 'str' },
      { name: 'condition_keywords', type: 'str' },
      { name: 'primary_condition', type: 'str' },
      { name: 'intervention_type', type: 'str' },
      { name: 'primary_intervention', type: 'str' },
      { name: 'intervention_name', type: 'str' },
      { name: 'intervention_arm_group_labels', type: 'str' },
      { name: 'intervention_description', type: 'str' },
      { name: 'has_data_monitoring_committee', type: 'object' },
      { name: 'responsible_party_investigator_affiliation', type: 'str' },
      { name: 'responsible_party_investigator_title', type: 'str' },
      { name: 'responsible_party_investigator_name', type: 'str' },
      { name: 'first_posted_date', type: 'datetime64[ns]' },
      { name: 'last_update_posted_date', type: 'datetime64[ns]' },
      { name: 'start_date', type: 'datetime64[ns]' },
      { name: 'study_completion_date', type: 'datetime64[ns]' },
      { name: 'primary_completion_date', type: 'datetime64[ns]' },
      { name: 'study_locations_city', type: 'str' },
      { name: 'study_locations_country', type: 'str' },
      { name: 'study_locations_geopoint', type: 'str' },
      { name: 'study_locations_facility', type: 'str' },
      { name: 'study_locations_zip', type: 'str' },
      { name: 'study_locations_state', type: 'str' },
      { name: 'standard_age_groups', type: 'str' },
      { name: 'sex', type: 'str' },
      { name: 'minimum_age', type: 'float64' },
      { name: 'maximum_age', type: 'float64' },
      { name: 'overall_status', type: 'str' },
      { name: 'status_category', type: 'str' },
      { name: 'status_verified_date', type: 'str' },
      { name: 'primary_outcomes_measures', type: 'str' },
      { name: 'primary_outcomes_timeframes', type: 'str' },
      { name: 'primary_outcomes_descriptions', type: 'str' },
      { name: 'secondary_outcomes_measures', type: 'str' },
      { name: 'secondary_outcomes_timeframes', type: 'str' },
      { name: 'secondary_outcomes_descriptions', type: 'str' },
      { name: 'has_results', type: 'bool' },
      { name: 'conditions', type: 'str' },
      { name: 'brief_summary', type: 'str' },
      { name: 'detailed_description', type: 'str' },
      { name: 'masking', type: 'str' },
      { name: 'allocation', type: 'str' },
      { name: 'intervention_model', type: 'str' },
      { name: 'primary_purpose', type: 'str' },
      { name: 'has_expanded_access', type: 'str' },
      { name: 'study_duration_days', type: 'float64' },
      { name: 'trial_duration', type: 'float64' },
      { name: 'references_type', type: 'str' },
      { name: 'references_citation', type: 'str' },
      { name: 'references_pmid', type: 'str' },
      { name: 'collaborators_name', type: 'str' },
      { name: 'collaborators_class', type: 'str' },
      { name: 'ipd_sharing', type: 'str' },
      { name: 'success_prediction', type: 'float64' },
      { name: 'economic_effect', type: 'float64' },
      { name: 'duration_prediction', type: 'float64' },
      { name: 'success_composite', type: 'float64' },
      { name: 'class', type: 'str' },
    ],
    columnCount: 77,
  },
  {
    slug: 'airbnb-data',
    name: 'AirBnB Data',
    category: 'sectorial',
    status: 'available',
    docsUrl:
      'https://docs.sov.ai/realtime-datasets/sectorial-datasets/airbnb-data',
    summary:
      'Short-let listing and occupancy measures by market. No sov.data() endpoint is registered in the SDK for this dataset.',
  },
];

// ---- Derived helpers --------------------------------------------------------

export const getDataset = (slug: string): Dataset | undefined =>
  datasets.find(d => d.slug === slug);

export const datasetsByCategory = (category: DatasetCategory): Dataset[] =>
  datasets.filter(d => d.category === category);

/**
 * Headline figures for the catalogue. Computed, never hardcoded — the numbers
 * on the homepage cannot drift from the registry.
 */
export const catalogueStats = () => {
  const withHistory = datasets.filter(d => d.historyStart);
  const earliest = withHistory.map(d => d.historyStart as string).sort()[0];
  const deepestEntities = Math.max(...datasets.map(d => d.entities ?? 0));
  return {
    total: datasets.length,
    documented: withHistory.length,
    beta: datasets.filter(d => d.status === 'beta').length,
    earliestHistory: earliest,
    deepestEntities,
    categories: {
      equity: datasetsByCategory('equity').length,
      economic: datasetsByCategory('economic').length,
      sectorial: datasetsByCategory('sectorial').length,
    },
  };
};

/** Fields a buyer diligences on, used to render an explicit completeness view. */
export const COVERAGE_FIELDS: Array<{
  key: keyof Dataset;
  label: string;
  why: string;
}> = [
  {
    key: 'historyStart',
    label: 'History start',
    why: 'Sizes the backtest window',
  },
  {
    key: 'entities',
    label: 'Entities covered',
    why: 'Sizes the cross-section',
  },
  {
    key: 'universe',
    label: 'Universe',
    why: 'Defines what is in and out of scope',
  },
  {
    key: 'updateFrequency',
    label: 'Update frequency',
    why: 'Determines strategy fit',
  },
  {
    key: 'publicationLag',
    label: 'Publication lag',
    why: 'Distinct from cadence — when it actually lands',
  },
  {
    key: 'pointInTime',
    label: 'Point-in-time',
    why: 'Without it, assume lookahead bias',
  },
  { key: 'identifiers', label: 'Identifiers', why: 'Determines joinability' },
  { key: 'rows', label: 'Row count', why: 'Sizes the data' },
  { key: 'schema', label: 'Schema', why: 'Column names and types' },
];
